import { Divider, Icon, ListItemButton, ListItemText } from "@mui/material";
import useGetPlants from "hooks/organization/plants/useGetPlants";
import useGetPlantLazy from "hooks/organization/plants/useGetPlantLazy";
import useDebouncedSearchTerm from "hooks/strings/useDebouncedSearchTerm";
import { useState } from "react";
import { centsToDollarsInput } from "../worksheetFormValues";
import PickerList from "./PickerList";
import PickerSearch from "./PickerSearch";
import LoadMoreButton from "./LoadMoreButton";
import { PickerProps } from "./types";

export default function PlantProductPicker({ onClose, onSelect }: PickerProps) {
  const { searchTerm, setSearchTerm, debouncedSearchTerm } = useDebouncedSearchTerm("");

  const { data, loading, paginatorInfo, fetchMore } = useGetPlants({
    first: 50,
    search: debouncedSearchTerm || undefined,
  });
  const [getPlant, { plant, loading: loadingProducts }] = useGetPlantLazy();
  const [selectedPlantId, setSelectedPlantId] = useState<string | null>(null);
  const plants = data ?? [];

  if (selectedPlantId) {
    return (
      <PickerList
        title="Select Plant Product"
        loading={loadingProducts}
        empty={(plant?.products ?? []).length === 0}
        onClose={onClose}
      >
        {(plant?.products ?? []).map((product) => (
          <ListItemButton
            key={product.id}
            onClick={() => {
              onSelect({
                sourceType: "plant_product",
                sourceId: product.id,
                sourceName: product.type,
                name: product.type,
                sourcePlantId: selectedPlantId,
                sourcePlantName: plant?.name ?? "",
                rate: centsToDollarsInput(product.rate ?? 0),
                unit: product.unit || "EACH",
                quantity: "1",
              });
              onClose();
            }}
          >
            <ListItemText
              primary={product.type}
              secondary={centsToDollarsInput(product.rate ?? 0)}
            />
          </ListItemButton>
        ))}
        <Divider />
        <ListItemButton onClick={() => setSelectedPlantId(null)}>
          <Icon fontSize="small" sx={{ mr: "4px" }}>
            arrow_back
          </Icon>
          <ListItemText primary="Back to plants" />
        </ListItemButton>
      </PickerList>
    );
  }

  return (
    <PickerList
      title="Select Plant"
      loading={loading}
      empty={plants.length === 0}
      header={
        <PickerSearch value={searchTerm} placeholder="Search plants" onChange={setSearchTerm} />
      }
      onClose={onClose}
    >
      {plants.map((plantOption) => (
        <ListItemButton
          key={plantOption.id}
          onClick={() => {
            setSelectedPlantId(plantOption.id);
            getPlant(plantOption.id);
          }}
        >
          <ListItemText primary={plantOption.name} />
        </ListItemButton>
      ))}
      {paginatorInfo?.hasMorePages && (
        <LoadMoreButton
          label="Load more plants"
          onClick={() =>
            fetchMore({
              page: (paginatorInfo?.currentPage ?? 1) + 1,
              updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult?.getPlants) {
                  return previous;
                }

                return {
                  ...fetchMoreResult,
                  getPlants: {
                    ...fetchMoreResult.getPlants,
                    data: [
                      ...(previous.getPlants?.data ?? []),
                      ...(fetchMoreResult.getPlants.data ?? []),
                    ],
                  },
                };
              },
            })
          }
        />
      )}
    </PickerList>
  );
}
